import { Box, Typography, IconButton, Select, FormControl, MenuItem, Divider, CircularProgress, TextField, Tooltip, SelectChangeEvent, FormHelperText } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { AddButton } from "../../button/button";
import ExpertSearchBar from "./expertSearchBar";
import { AppDispatch, RootState } from "../../../redux/store/appStore";
import { useDispatch, useSelector } from "react-redux";
import { STATUS } from "../../../utils/status";
import debounce from "lodash.debounce";
import { getExpert } from "../../../redux/slices/expertSlice/addExpertSlice";
import { getRoles, updateProject } from "../../../redux/slices/projectSlice/addProjectSlice";
import { useParams } from "react-router-dom";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface InviteEmailInterface {
    handleClose: () => void;
    alignment: string;
    selectedStage?: string;
}

const InviteExpertDrawer: React.FC<InviteEmailInterface> = ({
    handleClose, alignment, selectedStage
}) => {
    const [selectedOption, setSelectedOption] = useState(selectedStage || '');
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = useState<any[]>([]);
    const [loading] = useState(false);
    const { id } = useParams();
    const [selectedExperts, setSelectedExperts] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const experts = useSelector((state: RootState) => state.experts.experts);
    const getStatus = useSelector((state: RootState) => state.experts.getStatus);
    const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);
    const getReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
    const updateStatus = useSelector((state: RootState) => state.projects.updateStatus);
    const dispatch = useDispatch<AppDispatch>();
    const [taskLimit, setTaskLimit] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
    const stageRoles = useSelector((state: RootState) => state.projects.roles.results);

    useEffect(() => {
        dispatch(getExpert({
            search: searchTerm,
            paginate: false
        })
        );
    }, [dispatch, searchTerm]);

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (/^\d*$/.test(value)) {
            setTaskLimit(value);
        }
    };

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedCategory(typeof value === 'string' ? value.split(',') : value);
    };


    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value.trim());
    };

    const handleOptionChange = (event: any) => {
        setSelectedOption(event.target.value);
    };
    const debouncedResults = useMemo(() => { return debounce(handleSearchChange, 300); }, []);

    useEffect(() => {
        return () => {
            debouncedResults.cancel();
        };
    });

    useEffect(() => {
        dispatch(getRoles({ project_id: id || '', stage_id: selectedStage }))
    }, [dispatch, id, selectedStage]);
    const handleAddAllClick = () => {
        const payload = {
            id: id,
            assign: {
                [alignment === 'tasker' ? 'taskers' : 'reviewers']: selectedExperts.map(expert => (expert.expert)),
                stage_id: selectedOption,
                task_limit: taskLimit ? parseInt(taskLimit) : undefined,
                role_id: selectedCategory
            },
        };
        dispatch(updateProject(payload))
    };

    const handleAutocompleteInput = (e: any) => {
        debouncedResults(e)
    };

    useEffect(() => {
        if (getStatus === STATUS.SUCESS) {
            setOptions(experts.results)
        }
    }, [getStatus])

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                    sx={{
                        fontFamily: "Montserrat",
                        fontWeight: "600",
                        fontSize: "18px",
                        color: "#10383D",
                        marginBottom: "8px",
                    }}
                >
                    Onboard Experts
                </Typography>
                <IconButton onClick={handleClose} sx={{ padding: "4px" }}>
                    <CloseIcon sx={{ color: "#10383D" }} />
                </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <ExpertSearchBar
                open={open}
                handleOpen={() => setOpen(true)}
                handleClose={() => setOpen(false)}
                options={options}
                selectedExperts={selectedExperts}
                setSelectedExperts={setSelectedExperts}
                loading={loading}
                handleAutocompleteInput={handleAutocompleteInput}
            />

            <div style={{ display: "flex", flexDirection: "column", marginTop: 40 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                        sx={{
                            fontFamily: "Montserrat",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "24px",
                            letterSpacing: "0px",
                            textAlign: "left",
                            color: "#10383D",
                        }}
                    >
                        Onboarding Stage Name
                    </Typography>
                </Box>

                <FormControl fullWidth size="small" error={!getReviewStages?.results?.length}>
                    <Select
                        value={selectedOption}
                        onChange={handleOptionChange}
                        displayEmpty
                        disabled={getReviewStages?.results?.length === 0}
                    >
                        {getReviewStatus === STATUS.SUCESS &&
                            getReviewStages?.results?.map((option: any) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                    </Select>

                    {!getReviewStages?.results?.length && (
                        <FormHelperText>Create stages first before onboarding users.</FormHelperText>
                    )}
                </FormControl>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 40 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>
                        Auto Allocation Task Limit
                    </Typography>
                    <Tooltip title="Maximum number of tasks that can be auto-allocated on a specific stage to an expert (Tasker/Reviewer), based on task availability.">
                        <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }} />
                    </Tooltip>
                </Box>

                <TextField
                    placeholder="Auto Allocation Task Limit"
                    name="task_limit"
                    type="number"
                    size="small"
                    fullWidth
                    value={taskLimit}
                    onChange={handleFieldChange}
                />
            </div>

            {/* <div style={{ marginTop: 20 }}>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Role based on the stage selected</Typography>
                <Select
                    multiple
                    value={selectedCategory}
                    onChange={handleChange}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    renderValue={(selected) =>
                        selected.length === 0 ? 'Select Role' : stageRoles.results
                            .filter((role: { id: string; }) => selected.includes(role.id))
                            .map((role: { name: string; }) => role.name)
                            .join(', ')
                    }
                >
                    <MenuItem value="" disabled>Select Role</MenuItem>
                    {stageRoles?.results.length > 0 && stageRoles?.results.map((role: { id: string; name: string; }) => (
                        <MenuItem key={role.id} value={role.id}>
                            {role.name}
                        </MenuItem>
                    ))}
                </Select>
            </div> */}

            <Box sx={{ marginTop: "auto", paddingTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                {/* <AddButton disabled={(selectedValue === 'email' ? selectedExperts.length === 0: gigSource === '')|| taskLimit === '' || updateStatus === STATUS.PENDING} startIcon={updateStatus === STATUS.PENDING ? <CircularProgress size={15} /> : ''} onClick={handleAddAllClick} fullWidth> */}
                <AddButton disabled={selectedExperts.length === 0 || taskLimit === '' || updateStatus === STATUS.PENDING} startIcon={updateStatus === STATUS.PENDING ? <CircularProgress size={15} /> : ''} onClick={handleAddAllClick} fullWidth>
                    Onboard All
                </AddButton>
            </Box>
        </Box>
    );
};

export default InviteExpertDrawer;
